import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline panel rendered inside the "Próximos pasos" banner. Lets the
 * promoter answer the two collaborator-policy toggles right there without
 * navigating to the Configuration tab, and stamps the book with
 * `setup_decisions_made: true` so the banner disappears on confirm.
 */
export const SetupInlinePanel = ({ book, onConfirmed }) => {
  const initialPurchase = !!book?.order_info?.allow_collaborator_purchase;
  const initialReferrals = !!book?.order_info?.allow_collaborator_referrals;
  const [allowPurchase, setAllowPurchase] = useState(initialPurchase);
  const [allowReferrals, setAllowReferrals] = useState(initialReferrals);
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/books/${book.book_id}/confirm-setup-decisions`,
        {
          allow_collaborator_purchase: allowPurchase,
          allow_collaborator_referrals: allowReferrals,
        },
        { withCredentials: true }
      );
      toast.success('Decisiones guardadas');
      onConfirmed?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 space-y-3 p-3 rounded-md bg-white/70 border border-blue-200">
      <label className="flex items-center justify-between gap-3 text-xs cursor-pointer">
        <span>
          <span className="font-medium">¿Permites que los colaboradores compren ejemplares?</span>
          <span className="block text-[11px] text-muted-foreground mt-0.5">
            Si está activo, cada colaborador podrá pedir su propio libro tras enviar su aportación.
          </span>
        </span>
        <Switch
          checked={allowPurchase}
          onCheckedChange={setAllowPurchase}
          data-testid="setup-toggle-purchase"
        />
      </label>
      <label className="flex items-center justify-between gap-3 text-xs cursor-pointer">
        <span>
          <span className="font-medium">¿Aceptas propuestas de nuevos colaboradores?</span>
          <span className="block text-[11px] text-muted-foreground mt-0.5">
            Si está activo, los colaboradores podrán sugerir a otras personas para que también contribuyan.
          </span>
        </span>
        <Switch
          checked={allowReferrals}
          onCheckedChange={setAllowReferrals}
          data-testid="setup-toggle-referrals"
        />
      </label>
      <div className="flex items-center justify-end pt-1">
        <Button
          size="sm"
          onClick={confirm}
          disabled={saving}
          className="gap-1 h-8 text-xs"
          data-testid="setup-confirm-btn"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
          Confirmar decisiones
        </Button>
      </div>
    </div>
  );
};

export default SetupInlinePanel;
